import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { isTRPCClientError } from "@trpc/react-query";
import trpc from "../../utils/trpc.js";
import { useMediaQuery } from "usehooks-ts";
import { useLocation } from "react-router-dom";
import { ChatView } from "./chat-view.js";
import { HistoryView } from "./history-view.js";
import { CHAT_MODE_INFO, SOURCE_TO_MODE, type ChatMessage, type ChatMode, type ConversationRow } from "./helpers.js";
import { useChat } from "../../providers/chat.js";

export default function FloatingChat() {
  const { data: courseSettings } = trpc.course.getSettings.useQuery(undefined, { staleTime: Infinity });

  const { isOpen, setIsOpen, pendingRequest, clearPendingRequest } = useChat();
  const [isExpanded, setIsExpanded] = useState(false);
  const [view, setView] = useState<"chat" | "history">("chat");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [mode, setMode] = useState<ChatMode | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isResumingConversation, setIsResumingConversation] = useState(false);
  // The input bar stays hidden until the mode intro exchange has finished playing out.
  const [isModeIntroDone, setIsModeIntroDone] = useState(false);
  const [currentConversationTitle, setCurrentConversationTitle] = useState<string | null>(null);
  const [convCursor, setConvCursor] = useState<string | null>(null);
  const [allConversations, setAllConversations] = useState<ConversationRow[]>([]);
  const [convNextCursor, setConvNextCursor] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const historyListRef = useRef<HTMLDivElement>(null);
  const prevConvDataRef = useRef<typeof conversationsQuery.data | undefined>(undefined);
  const panelRef = useRef<HTMLDivElement>(null);
  // Bumped whenever the active conversation changes, so an in-flight send from a conversation
  // we've since switched away from can detect it's stale and avoid updating the wrong chat.
  const activeRequestIdRef = useRef(0);
  // Element to return focus to when the panel closes (the FAB, whichever way it was activated)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const sendChatMessageMutation = trpc.aier.sendChatMessage.useMutation();
  const sendChatArticleMessageMutation = trpc.aier.sendChatArticleMessage.useMutation();
  const renameConversationMutation = trpc.aier.renameConversation.useMutation();
  const deleteConversationMutation = trpc.aier.deleteConversation.useMutation();

  const conversationsQuery = trpc.aier.getConversations.useQuery(
    { page_size: 10, cursor: convCursor },
    { enabled: isOpen && view === "history", staleTime: 0 },
  );

  const conversationDetailQuery = trpc.aier.getConversationDetail.useQuery(
    { conversationId: conversationId ?? "" },
    { enabled: isResumingConversation && !!conversationId, staleTime: 0 },
  );

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isSending, isOpen]);

  // Auto-focus input once it's shown (chat view active, not resuming a past conversation, mode intro finished)
  useEffect(() => {
    if (!isOpen || view !== "chat" || isResumingConversation || !isModeIntroDone) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [isOpen, view, isResumingConversation, isModeIntroDone]);

  // Move focus into the dialog on open, and back to the trigger on close
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      panelRef.current?.focus();
    } else {
      previouslyFocusedRef.current?.focus();
    }
  }, [isOpen]);

  // Escape closes the dialog; Tab/Shift+Tab is trapped within it
  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsOpen(false);
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [setIsOpen],
  );

  // Scroll history list to top when switching to history view
  useEffect(() => {
    if (view === "history") {
      historyListRef.current?.scrollTo({ top: 0 });
    }
  }, [view]);

  // Accumulate paginated conversations
  useEffect(() => {
    if (!conversationsQuery.data || conversationsQuery.data === prevConvDataRef.current) return;
    prevConvDataRef.current = conversationsQuery.data;
    const results = conversationsQuery.data.results;
    setAllConversations(prev => (convCursor === null ? results : [...prev, ...results]));
    setConvNextCursor(conversationsQuery.data.next ?? null);
  }, [conversationsQuery.data, convCursor]);

  // Populate messages from conversation transcript
  useEffect(() => {
    // Bail while a (re)fetch is in flight so we don't apply a stale cached snapshot
    // and close the gate (isResumingConversation) before the fresh transcript arrives.
    if (!conversationDetailQuery.data || !isResumingConversation || !mode || conversationDetailQuery.isFetching) return;
    const { label, description } = CHAT_MODE_INFO[mode];
    const msgs: ChatMessage[] = [
      { id: "intro-human", kind: "human", text: label, created: conversationDetailQuery.data.created },
      { id: "intro-ai", kind: "ai", text: description, created: conversationDetailQuery.data.created },
      ...conversationDetailQuery.data.messages.map(m => ({
        id: m.id ?? Math.random().toString(36).slice(2),
        kind: m.kind,
        text: m.text,
        created: m.created,
        relatedSources: mode === "chat_article" ? m.relatedSources : undefined,
      })),
    ];
    setMessages(msgs);
    setIsResumingConversation(false);
  }, [conversationDetailQuery.data, conversationDetailQuery.isFetching, isResumingConversation, mode]);

  const resetConversationState = useCallback(() => {
    activeRequestIdRef.current += 1;
    setMessages([]);
    setConversationId(null);
    setMode(null);
    setCurrentConversationTitle(null);
    setIsResumingConversation(false);
    setIsModeIntroDone(false);
    setIsSending(false);
  }, []);

  const startNewChat = useCallback(() => {
    resetConversationState();
    setView("chat");
  }, [resetConversationState]);

  const handleToggle = () => setIsOpen(prev => !prev);

  const handleShowHistory = () => {
    setConvCursor(null);
    prevConvDataRef.current = undefined;
    setView("history");
    conversationsQuery.refetch();
  };

  const handleRenameConversation = async (id: string, title: string) => {
    await renameConversationMutation.mutateAsync({ conversationId: id, title });
    setAllConversations(prev => prev.map(c => (c.id === id ? { ...c, title } : c)));
    if (id === conversationId) setCurrentConversationTitle(title);
  };

  const handleDeleteConversation = async (id: string) => {
    await deleteConversationMutation.mutateAsync({ conversationId: id });
    setAllConversations(prev => prev.filter(c => c.id !== id));
    // Reset the active chat without leaving the history list when its conversation was just deleted
    if (id === conversationId) resetConversationState();
  };

  const sendMessage = async (text: string, sendMode: ChatMode, activeConversationId: string | null) => {
    // Snapshot which conversation this send belongs to, so we can detect if the user
    // switches to a different conversation before the response comes back.
    const requestId = activeRequestIdRef.current;
    setIsSending(true);
    setTimeout(() => inputRef.current?.focus(), 0);
    setMessages(prev => [
      ...prev,
      { id: Math.random().toString(36).slice(2), kind: "human", text, created: new Date().toISOString() },
    ]);
    try {
      const mutation = sendMode === "chat_article" ? sendChatArticleMessageMutation : sendChatMessageMutation;
      const result = await mutation.mutateAsync({
        conversationId: activeConversationId ?? undefined,
        userInput: text,
      });
      if (activeRequestIdRef.current !== requestId) return;
      if (!activeConversationId) {
        setConversationId(result.conversationId);
        setCurrentConversationTitle(result.title ?? null);
      }
      setMessages(prev => [
        ...prev,
        {
          id: result.messageId,
          kind: "ai",
          text: result.text,
          created: new Date().toISOString(),
          relatedSources: sendMode === "chat_article" ? result.relatedSources : undefined,
        },
      ]);
    } catch (error) {
      if (activeRequestIdRef.current !== requestId) return;
      const text =
        isTRPCClientError(error) && error.data?.code === "BAD_REQUEST"
          ? error.message
          : "Something went wrong sending your message. Please try again.";
      setMessages(prev => [
        ...prev,
        { id: Math.random().toString(36).slice(2), kind: "error", text, created: new Date().toISOString() },
      ]);
    } finally {
      if (activeRequestIdRef.current === requestId) setIsSending(false);
    }
  };

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || isSending || !mode) return;
    setInputText("");
    void sendMessage(text, mode, conversationId);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      e.currentTarget.focus();
    }
  };

  const handleSelectMode = (selected: ChatMode) => {
    setMode(selected);
    const { label, description } = CHAT_MODE_INFO[selected];
    setTimeout(() => {
      setMessages(prev => [
        ...prev,
        { id: Math.random().toString(36).slice(2), kind: "human", text: label, created: new Date().toISOString() },
      ]);
      setTimeout(() => {
        setMessages(prev => [
          ...prev,
          { id: Math.random().toString(36).slice(2), kind: "ai", text: description, created: new Date().toISOString() },
        ]);
        setIsModeIntroDone(true);
      }, 300);
    }, 300);
  };

  // Applies a request from an in-content "#openChat" link: always a fresh conversation in the requested mode
  const applyPendingRequest = (request: NonNullable<typeof pendingRequest>) => {
    resetConversationState();
    setView("chat");
    setInputText("");
    const requestMode = request.mode ?? "chat";
    if (!request.prompt) {
      handleSelectMode(requestMode);
      return;
    }
    const { label, description } = CHAT_MODE_INFO[requestMode];
    const created = new Date().toISOString();
    setMode(requestMode);
    setMessages([
      { id: Math.random().toString(36).slice(2), kind: "human", text: label, created },
      { id: Math.random().toString(36).slice(2), kind: "ai", text: description, created },
    ]);
    setIsModeIntroDone(true);
    void sendMessage(request.prompt, requestMode, null);
  };
  const applyPendingRequestRef = useRef(applyPendingRequest);
  applyPendingRequestRef.current = applyPendingRequest;

  useEffect(() => {
    if (!pendingRequest) return;
    clearPendingRequest();
    applyPendingRequestRef.current(pendingRequest);
  }, [pendingRequest, clearPendingRequest]);

  const handleSelectConversation = (conversation: ConversationRow) => {
    activeRequestIdRef.current += 1;
    setConversationId(conversation.id);
    setMode(SOURCE_TO_MODE[conversation.source]);
    setMessages([]);
    setIsResumingConversation(true);
    setIsModeIntroDone(true);
    setIsSending(false);
    setView("chat");
  };

  const isSendButtonDisabled = isSending || isResumingConversation || mode === null;
  const conversationTitle =
    currentConversationTitle ||
    conversationDetailQuery.data?.title ||
    allConversations.find(c => c.id === conversationId)?.title ||
    null;
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isBeforeLg = useMediaQuery("(max-width: 1023px)");
  const { search } = useLocation();
  // Raise FAB above the fixed "next item" panel present on learning item pages (fixed below lg)
  const fabBottom = isBeforeLg && new URLSearchParams(search).has("module_item_id") ? "bottom-20" : "bottom-6";

  if (!(courseSettings?.chatEnabled ?? true)) return null;

  return createPortal(
    <div
      className={
        isMobile
          ? "pointer-events-none fixed inset-0 z-50"
          : `pointer-events-none fixed ${fabBottom} right-6 z-50 flex flex-col items-end gap-3`
      }>
      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chat-panel"
            layout
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            ref={panelRef}
            tabIndex={-1}
            onKeyDown={handlePanelKeyDown}
            className={`pointer-events-auto flex flex-col overflow-hidden bg-white shadow-2xl outline-none ${
              isMobile ? "absolute inset-0 rounded-none" : "rounded-2xl ring-1 ring-black/5"
            }`}
            style={
              isMobile
                ? {}
                : {
                    width: isExpanded ? 720 : 360,
                    height: isExpanded ? 860 : 560,
                    maxWidth: "calc(100vw - 48px)",
                    maxHeight: "calc(100vh - 110px)",
                  }
            }>
            {view === "chat" ? (
              <ChatView
                key={activeRequestIdRef.current}
                messages={messages}
                mode={mode}
                showInput={isModeIntroDone}
                isSending={isSending}
                isResumingConversation={isResumingConversation}
                inputText={inputText}
                isSendButtonDisabled={isSendButtonDisabled}
                isMobile={isMobile}
                isExpanded={isExpanded}
                messagesEndRef={messagesEndRef}
                inputRef={inputRef}
                conversationTitle={conversationTitle}
                onShowHistory={handleShowHistory}
                onNewChat={startNewChat}
                onSelectMode={handleSelectMode}
                onToggleExpanded={() => setIsExpanded(prev => !prev)}
                onClose={handleToggle}
                onInputChange={setInputText}
                onKeyDown={handleKeyDown}
                onSend={handleSend}
              />
            ) : (
              <HistoryView
                conversations={allConversations}
                isLoading={conversationsQuery.isLoading}
                isFetching={conversationsQuery.isFetching}
                nextCursor={convNextCursor}
                isMobile={isMobile}
                isExpanded={isExpanded}
                historyListRef={historyListRef}
                onSelectConversation={handleSelectConversation}
                onNewChat={startNewChat}
                onLoadMore={() => setConvCursor(convNextCursor)}
                onToggleExpanded={() => setIsExpanded(prev => !prev)}
                onClose={handleToggle}
                onRenameConversation={handleRenameConversation}
                onDeleteConversation={handleDeleteConversation}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB */}
      <div
        className={
          isMobile ? (isOpen ? "hidden" : `pointer-events-auto absolute ${fabBottom} right-6`) : "pointer-events-auto"
        }>
        <button
          type="button"
          onClick={handleToggle}
          className="flex h-[60px] w-[60px] items-center justify-center rounded-full bg-gradient-to-br from-lac-leman to-lac-leman-80 shadow-lg transition hover:brightness-110"
          aria-label={isOpen ? "Close chat" : "Open chat"}>
          <AnimatePresence mode="wait" initial={false}>
            {isOpen ? (
              <motion.span
                key="close"
                initial={{ opacity: 0, rotate: -90, scale: 0.7 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 90, scale: 0.7 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="flex items-center justify-center">
                <XMarkIcon className="h-7 w-7 text-white" />
              </motion.span>
            ) : (
              <motion.span
                key="open"
                initial={{ opacity: 0, rotate: 90, scale: 0.7 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: -90, scale: 0.7 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="flex items-center justify-center">
                <SparklesIcon className="h-7 w-7 text-white" />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </div>,
    document.body,
  );
}
